import { useEffect, useState } from 'react';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useTraits } from '@atlassian/jira-traits/src/TraitsStore.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { SITE_USER } from '@atlassian/jira-traits/src/constants.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { hasTrait } from '@atlassian/jira-traits/src/utils.tsx';
import { useIsJiraNewUserTraitCrossFlowSelector } from './useIsJiraNewUserTraitCrossFlowSelector.tsx';

export const useYbrIsNewUserCrossFlow = ({
	crossFlowProduct = 'jpd',
}: {
	crossFlowProduct?: 'jpd' | 'confluence';
} = {}) => {
	const cloudId = useCloudId();
	const accountId = useAccountId();
	const [traits, { fetchTraits }] = useTraits();
	const [isJiraNewUser] = useIsJiraNewUserTraitCrossFlowSelector({ crossFlowProduct });

	const [isYbrNewUser, setIsYbrNewUser] = useState(true);

	useEffect(() => {
		if (!accountId || !cloudId || !fg('jira_ybr_precondition_check_rollout_cross_flow')) {
			setIsYbrNewUser(false);
			return;
		}

		fetchTraits([SITE_USER], { cloudId, accountId });
	}, [accountId, cloudId, fetchTraits]);

	useEffect(() => {
		// Wait until SITE_USER traits are loaded and jira_first_active trait is loaded before checking if user is actually a new user
		if (
			!fg('jira_ybr_precondition_check_rollout_cross_flow') ||
			!traits[SITE_USER]?.loaded ||
			!traits[SITE_USER]?.attributes?.length ||
			!hasTrait(traits, 'jira_first_active')
		) {
			return;
		}

		// Only expose event if user is a new user
		if (
			crossFlowProduct === 'jpd' &&
			(!isJiraNewUser || !expVal('jira_ybr_first_batch_cross_flow', 'isYBREnabled', false))
		) {
			// If user is not a new user or the experiment is not enabled, user is not a YBR new user
			// which mean the message will be show as normal
			setIsYbrNewUser(false);
		}

		if (
			crossFlowProduct === 'confluence' &&
			(!isJiraNewUser ||
				!expVal('jira_ybr_first_batch_cross_flow_confluence', 'isYBREnabled', false))
		) {
			setIsYbrNewUser(false);
		}
	}, [isJiraNewUser, traits, crossFlowProduct]);

	return [isYbrNewUser];
};
